/**
 * Enforce a per-tool wall-clock ceiling by RACING the call, not by asking it to stop.
 *
 * ## Why the SDK's own `toolMs` is not enough here
 *
 * AI SDK v7 does support per-tool timeouts, and `./budgets` passes them. But they are
 * COOPERATIVE: the SDK aborts an `AbortSignal` and hands it to the tool as
 * `options.abortSignal`. Work that never reads that signal is not interrupted — the `await`
 * simply keeps waiting. Measured on this package when #2451 landed: **zero** files under
 * `src/lib/tools/` reference `abortSignal`, and zero under `apps/hub/lib/core/chat/tools/`.
 *
 * So configuring `toolMs` alone would have been the exact failure this codebase keeps
 * re-learning — infrastructure that is present, typed, green in review, and does nothing. The
 * ceiling would read as enforced while a hung Google call still burned the whole function
 * budget. This module is the half that actually bites: it races the tool's promise against a
 * timer and gives up on the call, whether or not the call agrees.
 *
 * The SDK config stays anyway. It costs nothing, it bounds any tool that IS abort-aware, and it
 * starts paying automatically the day one becomes so.
 *
 * ## What "giving up" does and does not mean
 *
 * JavaScript cannot cancel a promise. The abandoned work keeps running until it finishes or the
 * function dies — this bounds how long the TURN waits, not how long the call takes. That is the
 * whole requirement: the turn must be able to move on and answer.
 *
 * ## The result is a value, never a throw
 *
 * A timed-out tool returns `{ error }` (`MERIDIAN_SURFACE_PARITY_AND_MODULE_SIZE`,
 * `executors/mutation-result-contract.ts`). A throw would be serialised for the model as
 * `JSON.stringify(error)` — `{}` — which does not read as a failure at all, so the model either
 * reports success or repeats the identical hanging call. The message names the tool and the
 * budget it blew and tells the model what to do instead, so the turn degrades into a usable
 * partial answer. It is never the literal "unknown error" (`TOOL_ERROR_FALLBACK`).
 *
 * ## Prompt caching is unaffected
 *
 * Only `execute` is replaced. A tool's cached prefix is its NAME, DESCRIPTION and INPUT SCHEMA —
 * the parts serialised into the request — and none of them is touched here. `execute` is
 * server-side and never sent to the model, so wrapping every tool invalidates nothing
 * (`MERIDIAN_PROMPT_CACHING`).
 */

import type { Tool } from 'ai';
import { toolErrorResult } from '@west-tennessee-consulting/tool-results';

import { logger } from '../logger.js';

/** Sentinel resolved by the timer leg of the race. Not an error — errors mean the tool failed. */
const TIMED_OUT: unique symbol = Symbol('meridian.tool.timed-out');

/**
 * Raised only to give `logger.error` a real Error to serialise, so #Hub Errors gets a named
 * failure with a stack instead of a bare string. It is NEVER thrown out of `execute`.
 */
export class ToolTimeoutError extends Error {
  readonly toolName: string;
  readonly budgetMs: number;

  constructor(toolName: string, budgetMs: number) {
    super(`Tool "${toolName}" exceeded its ${budgetMs}ms wall-clock budget.`);
    this.name = 'ToolTimeoutError';
    this.toolName = toolName;
    this.budgetMs = budgetMs;
  }
}

export interface ToolTimeoutOptions {
  /** Ceiling applied to any tool without a specific entry in {@link perToolMs}. */
  readonly defaultMs: number;
  /** Per-tool ceilings, keyed by BARE tool name (not the SDK's `<name>Ms` form). */
  readonly perToolMs?: Readonly<Record<string, number>>;
  /** Correlation fields merged into the timeout log line (chatId, userId, surface, agent…). */
  readonly logContext?: Readonly<Record<string, unknown>>;
}

/** A tool's `execute`, in the loosest shape this module needs. Never `any`. */
type ToolExecute = (input: unknown, options: unknown) => unknown;

/**
 * Narrow a tool's `execute` to something callable.
 *
 * A bare `typeof x === 'function'` narrows `unknown` to `Function`, which TypeScript refuses to
 * call with arguments — a type GUARD at this boundary is the sanctioned way across, not a cast.
 */
function isToolExecute(value: unknown): value is ToolExecute {
  return typeof value === 'function';
}

function isAsyncIterable(value: unknown): value is AsyncIterable<unknown> {
  return (
    typeof value === 'object' &&
    value !== null &&
    Symbol.asyncIterator in value &&
    typeof (value as AsyncIterable<unknown>)[Symbol.asyncIterator] === 'function'
  );
}

/**
 * Wrap every executable tool in `tools` with its wall-clock ceiling.
 *
 * Tools with no `execute` are returned UNTOUCHED, and that is load-bearing in two ways. A HITL
 * tool has no `execute` in stream mode — its callable form is the approval executor — and the
 * SDK tests for the key's PRESENCE, so adding one would silently convert an approval-gated tool
 * into a directly-callable one. Provider-executed server tools (`tool_search`, code execution)
 * likewise have no `execute`: they run on Anthropic's side, where our timer has no reach.
 */
export function withToolTimeouts(
  tools: Record<string, unknown>,
  options: ToolTimeoutOptions
): Record<string, unknown> {
  const wrapped: Record<string, unknown> = {};
  for (const [name, candidate] of Object.entries(tools)) {
    wrapped[name] = applyTimeout(name, candidate, options);
  }
  return wrapped;
}

/**
 * Typed single-tool form, for callers that hold a real `Tool` and must hand one back.
 *
 * It exists because the streaming path's tool map is typed `Record<string, unknown>` (its
 * assembly helpers predate this and return that), while the agent path holds a proper
 * `Record<string, Tool>` it must pass to `generateText`. One shared implementation with two entry
 * points keeps both call sites cast-free — the alternative was a single generic whose return had
 * to be asserted back to the input type, i.e. a cast standing in for a guarantee.
 */
export function withToolTimeout(tool: Tool, options: ToolTimeoutOptions & { toolName: string }): Tool {
  const original: unknown = tool.execute;
  if (!isToolExecute(original)) return tool;

  const { execute: _replaced, ...rest } = tool;
  return {
    ...rest,
    execute: buildTimedExecute({
      toolName: options.toolName,
      budgetMs: options.perToolMs?.[options.toolName] ?? options.defaultMs,
      original,
      logContext: options.logContext,
    }),
  };
}

/** Wrap one entry of a loosely-typed tool map, leaving anything without an `execute` untouched. */
function applyTimeout(name: string, candidate: unknown, options: ToolTimeoutOptions): unknown {
  if (typeof candidate !== 'object' || candidate === null) return candidate;

  const original: unknown = (candidate as { execute?: unknown }).execute;
  if (!isToolExecute(original)) return candidate;

  return {
    ...candidate,
    execute: buildTimedExecute({
      toolName: name,
      budgetMs: options.perToolMs?.[name] ?? options.defaultMs,
      original,
      logContext: options.logContext,
    }),
  };
}

function buildTimedExecute(params: {
  toolName: string;
  budgetMs: number;
  original: ToolExecute;
  logContext?: Readonly<Record<string, unknown>>;
}): ToolExecute {
  const { toolName, budgetMs, original, logContext } = params;

  return (input: unknown, executionOptions: unknown): unknown => {
    const started = original(input, executionOptions);

    // A streaming tool yields parts as it goes; racing it would consume the iterator and the
    // caller would get a resolved sentinel instead of a stream. No Meridian or Hub tool returns
    // one today, but breaking one silently later is worse than leaving it unbounded and saying so.
    if (isAsyncIterable(started)) {
      return started;
    }

    return raceAgainstBudget({ toolName, budgetMs, work: started, logContext });
  };
}

async function raceAgainstBudget(params: {
  toolName: string;
  budgetMs: number;
  work: unknown;
  logContext?: Readonly<Record<string, unknown>>;
}): Promise<unknown> {
  const { toolName, budgetMs, work, logContext } = params;

  // Not `Promise.resolve(work)` alone: a tool that returns a plain value resolves immediately and
  // never reaches the timer, which is correct and free.
  const settled = Promise.resolve(work);

  let timer: ReturnType<typeof setTimeout> | undefined;
  const expiry = new Promise<typeof TIMED_OUT>((resolve) => {
    timer = setTimeout(() => resolve(TIMED_OUT), budgetMs);
  });

  try {
    const outcome = await Promise.race([settled, expiry]);
    if (outcome !== TIMED_OUT) {
      return outcome;
    }

    // The abandoned call is still in flight and nothing will await it again. Without this the
    // eventual rejection is an unhandledRejection that crashes the process on some runtimes and
    // is pure noise on the rest — the failure is already reported below.
    settled.catch(() => undefined);

    const error = new ToolTimeoutError(toolName, budgetMs);
    // Raw error SECOND (LOGGER_ERROR_SLOT): a context bag in that slot compiles, reads like every
    // other structured log, and empties `entry.context` while the cause never reaches the card.
    logger.error('meridian tool exceeded its wall-clock budget', error, {
      op: 'withToolTimeouts',
      tool: toolName,
      budgetMs,
      ...logContext,
    });

    return toolErrorResult(
      toolName,
      new Error(
        `Timed out after ${budgetMs}ms. The call was abandoned, so nothing was read or changed ` +
          `by it. Do not repeat it unchanged — narrow the arguments (a smaller range, a more ` +
          `specific query), use a different tool, or tell the user this lookup is not responding.`
      )
    );
  } finally {
    // RESOURCE_CLEANUP_AND_BOUNDED_CACHES: the timer must die on the fast path too, or every
    // completed tool call pins a pending timer until its full budget elapses.
    if (timer !== undefined) clearTimeout(timer);
  }
}
